import React, { useState } from 'react';
import { AlertCircle, ChevronDown, ChevronUp, Gift, Info } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { formatCentsToDollars } from '@/services/childSpendingService';
import { useNavigate } from 'react-router-dom';

interface ChildSpendingLimitBannerProps {
  currentSpentCents: number;
  limitCents: number;
  remainingCents: number;
  percentageUsed: number;
  isAtLimit: boolean;
  onAskParentClick?: () => void;
}

const ChildSpendingLimitBanner: React.FC<ChildSpendingLimitBannerProps> = ({
  currentSpentCents,
  limitCents,
  remainingCents,
  percentageUsed,
  isAtLimit,
  onAskParentClick
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const getProgressColor = () => {
    if (percentageUsed >= 100) return 'bg-destructive';
    if (percentageUsed >= 80) return 'bg-warning';
    return 'bg-primary';
  };

  const handleFaqClick = () => {
    navigate('/faq#parent-child-accounts');
  };

  return (
    <Alert 
      className={`mb-6 ${isAtLimit ? 'border-destructive bg-destructive/10' : 'border-warning bg-warning/10'}`}
    >
      <AlertCircle className={`h-5 w-5 ${isAtLimit ? 'text-destructive' : 'text-warning'}`} />
      <div className="flex-1">
        <AlertTitle className="flex items-center justify-between">
          <span>
            {isAtLimit 
              ? '🚫 Spending Limit Reached' 
              : '👶 Under-13 Spending Limit'}
          </span>
        </AlertTitle>
        <AlertDescription className="mt-2">
          {/* Progress bar */}
          <div className="mb-3">
            <div className="flex justify-between text-sm mb-1">
              <span>Spent: {formatCentsToDollars(currentSpentCents)}</span>
              <span>Limit: {formatCentsToDollars(limitCents)}</span>
            </div>
            <Progress value={percentageUsed} className="h-2" />
            <div className="text-sm mt-1 text-muted-foreground">
              {isAtLimit 
                ? 'You have reached your $50 spending limit'
                : `${formatCentsToDollars(remainingCents)} remaining`
              }
            </div>
          </div>

          {/* Message */}
          <p className="text-sm mb-3">
            {isAtLimit 
              ? 'Ask your parent or guardian to gift you points instead! They can purchase points and send them to your account.'
              : 'Under-13 accounts have a $50 lifetime spending limit to keep you safe. Your parent can gift you more points anytime!'}
          </p>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2 mb-3">
            {onAskParentClick && (
              <Button 
                size="sm" 
                variant={isAtLimit ? 'default' : 'outline'}
                onClick={onAskParentClick}
                className="gap-1"
              >
                <Gift className="h-4 w-4" />
                Ask Parent for Points
              </Button>
            )}
            <Button 
              size="sm" 
              variant="ghost"
              onClick={handleFaqClick}
              className="gap-1"
            >
              <Info className="h-4 w-4" />
              Learn More
            </Button>
          </div>

          {/* Expandable "Why is there a limit?" section */}
          <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="link" size="sm" className="p-0 h-auto text-muted-foreground gap-1">
                {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                Why is there a limit?
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 p-3 bg-muted/50 rounded-md text-sm">
              <p className="mb-2">
                <strong>To protect families:</strong> Parents are only legally liable for up to $50 
                of purchases made by children under 13. This limit ensures:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Parents maintain control over spending</li>
                <li>Kids can't accidentally overspend</li>
                <li>Families are protected from unexpected charges</li>
              </ul>
              <p className="mt-2">
                Once you reach the limit, your parent can purchase points and gift them to you 
                through the <strong>Parent Dashboard</strong>. This way, they always know what's 
                being spent!
              </p>
            </CollapsibleContent>
          </Collapsible>
        </AlertDescription>
      </div>
    </Alert>
  );
};

export default ChildSpendingLimitBanner;
